import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../../authContext/context';
import { getRequest, postRequest } from '../../lib/api';
import BreadcrumbsWrapper from '../../components/breadcrumbs';
import Swal from 'sweetalert2';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  CircularProgress,
  ToggleButton,
  InputAdornment
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

// Customer Search Modal Component
function CustomerSearchModal({ show, onHide, authToken, onCustomerAdd, selectedCustomers }) {
  const [searchFields, setSearchFields] = useState({
    title: '',
    forename: '',
    surname: '',
    email: '',
    phone_number: ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = React.useRef();

  const fetchCustomerSearch = async () => {
    const isEmpty = Object.values(searchFields).every(v => !v);
    if (isEmpty) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      const res = await postRequest('private/leadsCustomerList', searchFields, authToken);
      setSearchResults(res?.data || []);
    } catch (err) {
      console.error('Error fetching search:', err);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchCustomerSearch, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchFields]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearchFields((prev) => ({ ...prev, [name]: value }));
  };

  const isCustomerAdded = (customerId) => {
    return selectedCustomers.some(c => (c._id || c) === customerId);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Add Purchaser</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className='m-0'>
          {['title', 'forename', 'surname', 'email', 'phone_number'].map((field, idx) => (
            <Col xs={field === 'email' || field === 'phone_number' ? 6 : 4} key={idx}>
              <Box sx={{ '& > :not(style)': { m: 1, width: '100%' } }}>
                <TextField
                  label={field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' ')}
                  name={field}
                  value={searchFields[field]}
                  onChange={handleChange}
                  variant="outlined"
                  type={field === 'email' ? 'email' : field === 'phone_number' ? 'tel' : 'text'}
                />
              </Box>
            </Col>
          ))}
        </Row>

        <Row className='mt-4'>
          <Col>
            <Table responsive bordered>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Add</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="4" className='text-center'>Loading...</td></tr>
                ) : searchResults.length > 0 ? (
                  searchResults.map((customer) => (
                    <tr key={customer._id}>
                      <td>{customer.title} {customer.forename} {customer.surname}</td>
                      <td>{customer.email}</td>
                      <td>{customer.phone_number}</td>
                      <td>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={isCustomerAdded(customer._id)}
                          onClick={() => {
                            onCustomerAdd(customer);
                            onHide();
                          }}
                        >
                          {isCustomerAdded(customer._id) ? 'Added' : 'Add'}
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="4" className='text-center'>No customers found</td></tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

export default function EditConveyancingForm() {
  const navigate = useNavigate();
  const { lead_id } = useParams();
  const authCtx = useContext(AuthContext);
  
  // State variables
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [leadData, setLeadData] = useState(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [hasConveyancingData, setHasConveyancingData] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    // Property Address
    line1: '',
    line2: '',
    town: '',
    county: '',
    postcode: '',

    // Property Details
    property_value: '',
    ownership_type: 'Freehold',
    completion_date: '',

    // Partner & Lender
    partner: '',
    lender_panel: '',

    // Purchasers
    purchasers: [],

    // Info fields
    firstTimeBuyer: false,
    helpToBuyISA: false,
    helpToBuy: false,
    helpToBuyEquityLoan: false,
    rightToBuy: false,
    sharedOwnership: false,
    buyingWithMortgage: false,
    newBuild: false,
    islamicMortgage: false,
    auctionRepossession: false,
    giftedDeposit: '',
    multipleProperties: false,
    buyToLet: false,
    arrangingIndemnityPolicy: false,
    unregisteredProperty: false,
    staircasing: false,
    isNonUKResident: false,
    nonResidentialMixed: false
  });

  // Fetch lead data on mount
  useEffect(() => {
    fetchLeadData();
  }, [lead_id]);

  const fetchLeadData = async () => {
    try {
      setLoading(true);
      console.log('Fetching conveyancing lead data for ID:', lead_id);
      
      let response;
      try {
        // Try the correct purchase edit endpoint first
        response = await getRequest(`private/getEditDataPurchaseConveyancing/${lead_id}`);
        console.log('✅ Purchase edit endpoint response:', response);
      } catch (error) {
        console.log('❌ Primary endpoint failed:', error.message);
        try {
          console.log('📡 Trying fallback endpoint: getConveyancingLead...');
          // Fallback to generic conveyancing lead endpoint
          response = await getRequest(`private/getConveyancingLead/${lead_id}`);
        } catch (fallbackError) {
          console.log('Fallback endpoint also failed:', fallbackError);
          console.log('Trying purchase details endpoint...');
          // Last resort - try purchase details endpoint
          try {
            response = await getRequest(`private/purchase-details/${lead_id}`);
          } catch (purchaseError) {
            console.log('Purchase details endpoint also failed:', purchaseError);
            // If all endpoints fail, it might mean no conveyancing data exists yet
            // Try to get basic lead data
            try {
              response = await postRequest(`private/get-single-lead/${lead_id}`, {}, authCtx.token);
              if (response?.status === 200 && response?.data) {
                // Transform basic lead data to match expected structure
                response.data = {
                  lead_id: response.data.lead_id || lead_id,
                  customers: response.data.customers || [],
                  customer_ids: response.data.customers?.map(c => c._id || c) || [],
                  property_address: response.data.property_address || response.data.address || {},
                  property_value: response.data.property_value || '',
                  ownership_type: response.data.ownership_type || 'Freehold',
                  completion_date: response.data.completion_date || '',
                  // Initialize all info fields as false
                  first_time_buyer: false,
                  help_to_buy_isa: false,
                  help_to_buy: false,
                  help_to_buy_equity_loan: false,
                  right_to_buy: false,
                  shared_ownership: false,
                  buying_with_mortgage: false,
                  new_build: false,
                  islamic_mortgage: false,
                  auction_repossession: false,
                  gifted_deposit: '',
                  multiple_properties: false,
                  buy_to_let: false,
                  arranging_indemnity_policy: false,
                  unregistered_property: false,
                  staircasing: false,
                  is_non_uk_resident: false,
                  non_uk_resident: false,
                  non_residential_mixed: false,
                  non_residential_mixed_use: false
                };
              }
            } catch (leadError) {
              console.error('Could not fetch basic lead data:', leadError);
              throw new Error('No lead or conveyancing data found');
            }
          }
        }
      }
      
      if (response && (response.status === 200 || response.status === 'success') && response.data) {
        const data = response.data;
        console.log('Lead data received:', data);
        console.log('Property address structure:', data.property_address);
        console.log('Customer IDs:', data.customer_ids);
        console.log('Customers:', data.customers);
        console.log('Info object:', data.info);
        console.log('Raw info field values:', {
          first_time_buyer: data.first_time_buyer,
          help_to_buy_isa: data.help_to_buy_isa,
          help_to_buy: data.help_to_buy,
          help_to_buy_equity_loan: data.help_to_buy_equity_loan,
          right_to_buy: data.right_to_buy,
          shared_ownership: data.shared_ownership,
          buying_with_mortgage: data.buying_with_mortgage,
          new_build: data.new_build,
          islamic_mortgage: data.islamic_mortgage,
          auction_repossession: data.auction_repossession,
          gifted_deposit: data.gifted_deposit,
          multiple_properties: data.multiple_properties,
          buy_to_let: data.buy_to_let,
          arranging_indemnity_policy: data.arranging_indemnity_policy,
          unregistered_property: data.unregistered_property,
          staircasing: data.staircasing,
          is_non_uk_resident: data.is_non_uk_resident,
          non_residential_mixed: data.non_residential_mixed
        });
        
        // Check if this is actual conveyancing data or just basic lead data
        // We need to check if this lead has been saved as conveyancing before
        if (data.conveyancing_branch_lead_id || data.conveyancing_id || data.has_conveyancing_data) {
          setHasConveyancingData(true);
          console.log('✅ Found existing conveyancing data');
        } else {
          setHasConveyancingData(false);
          console.log('❌ No existing conveyancing data - will create new');
        }
        
        setLeadData(data);
        
        // Auto-fill form data with better data structure handling
        const autoFillData = {
          // Property Address - handle both object and string formats
          line1: '',
          line2: '',
          town: '',
          county: '',
          postcode: '',
          
          // Check if property_address exists and is an object
          ...(data.property_address && typeof data.property_address === 'object' ? {
            line1: data.property_address.address1 || '',
            line2: data.property_address.address2 || '',
            town: data.property_address.town || '',
            county: data.property_address.country || data.property_address.county || '', // Handle both 'country' and 'county'
            postcode: data.property_address.postcode || ''
          } : {}),
          
          // Check if property_address is a string (legacy format)
          ...(data.property_address && typeof data.property_address === 'string' ? (() => {
            const parts = data.property_address.split(',').map(p => p.trim());
            return {
              line1: parts[0] || '',
              line2: parts[1] || '',
              town: parts[2] || '',
              county: parts[3] || '',
              postcode: parts[parts.length - 1] || ''
            };
          })() : {}),
          
          // Property Details
          property_value: data.property_value || data.purchase_price || data.net_value || '',
          ownership_type: data.ownership_type || (data.leasehold === true ? 'Leasehold' : 'Freehold'),
          completion_date: (data.completion_date || data.est_completion_date)
            ? new Date(data.completion_date || data.est_completion_date).toISOString().split('T')[0]
            : '',

          // Partner & Lender
          partner: data.partner || '',
          lender_panel: data.lender_panel || '',

          // Purchasers - ensure it's an array and handle populated data
          purchasers: Array.isArray(data.customer_ids)
            ? data.customer_ids
            : (Array.isArray(data.customers) ? data.customers : []),
          
          // Info fields - check both snake_case fields and info object
          firstTimeBuyer: data.first_time_buyer === true || data.info?.firstTimeBuyer === true || data.conveyancing_details?.first_time_buyer === true,
          helpToBuyISA: data.help_to_buy_isa === true || data.info?.helpToBuyISA === true || data.conveyancing_details?.help_to_buy_isa === true,
          helpToBuy: data.help_to_buy === true || data.info?.helpToBuy === true || data.conveyancing_details?.help_to_buy === true,
          helpToBuyEquityLoan: data.help_to_buy_equity_loan === true || data.info?.helpToBuyEquityLoan === true || data.conveyancing_details?.help_to_buy_equity_loan === true,
          rightToBuy: data.right_to_buy === true || data.info?.rightToBuy === true || data.conveyancing_details?.right_to_buy === true,
          sharedOwnership: data.shared_ownership === true || data.info?.sharedOwnership === true || data.conveyancing_details?.shared_ownership === true,
          buyingWithMortgage: data.buying_with_mortgage === true || data.info?.buyingWithMortgage === true || data.conveyancing_details?.buying_with_mortgage === true || Boolean(data.mortgage_lender),
          newBuild: data.new_build === true || data.info?.newBuild === true || data.conveyancing_details?.new_build === true,
          islamicMortgage: data.islamic_mortgage === true || data.info?.islamicMortgage === true || data.conveyancing_details?.islamic_mortgage === true,
          auctionRepossession: data.auction_repossession === true || data.info?.auctionRepossession === true || data.conveyancing_details?.auction_repossession === true,
          giftedDeposit: data.gifted_deposit && data.gifted_deposit !== 'No' ? data.gifted_deposit : 
                         data.info?.giftedDeposit && data.info.giftedDeposit !== 'No' ? data.info.giftedDeposit : '',
          multipleProperties: data.multiple_properties === true || data.info?.multipleProperties === 'Yes' || data.conveyancing_details?.multiple_properties === true,
          buyToLet: data.buy_to_let === true || data.info?.buyToLet === true || data.conveyancing_details?.buy_to_let === true,
          arrangingIndemnityPolicy: data.arranging_indemnity_policy === true || data.info?.arrangingIndemnityPolicy === true || data.conveyancing_details?.arranging_indemnity_policy === true,
          unregisteredProperty: data.unregistered_property === true || data.info?.unregisteredProperty === true || data.conveyancing_details?.unregistered_property === true,
          staircasing: data.staircasing === true || data.info?.staircasing === 'Yes' || data.conveyancing_details?.staircasing === true,
          isNonUKResident: data.is_non_uk_resident === true || data.non_uk_resident === true || data.info?.isNonUKResident === 'Yes' || data.conveyancing_details?.is_non_uk_resident === true,
          nonResidentialMixed: data.non_residential_mixed === true || data.non_residential_mixed_use === true || data.info?.nonResidentialMixed === true || data.conveyancing_details?.non_residential_mixed === true
        };
        
        console.log('Auto-fill data prepared:', autoFillData);
        setFormData(autoFillData);
        
        // Log specific fields for debugging
        console.log('✅ Auto-fill complete! Form data set to:');
        console.log('📍 Address:', `${autoFillData.line1}, ${autoFillData.town}, ${autoFillData.postcode}`);
        console.log('💰 Property Value:', autoFillData.property_value);
        console.log('🏗️ Ownership Type:', autoFillData.ownership_type);
        console.log('📅 Completion Date:', autoFillData.completion_date);
        console.log('👥 Purchasers:', autoFillData.purchasers.length);
        console.log('☑️ Info fields set:', Object.keys(autoFillData).filter(key => key.includes('Buy') || key.includes('Time')).map(key => `${key}: ${autoFillData[key]}`));
      }
    } catch (error) {
      console.error('Error fetching lead data:', error);
      
      // If it's a not found error, show a more specific message
      if (error.message === 'No lead or conveyancing data found') {
        setHasConveyancingData(false); // Make sure we're in create mode
        Swal.fire({
          icon: 'info',
          title: 'No Conveyancing Data',
          text: 'No existing conveyancing data found. You can start filling the form.'
        });
        // Set empty form data
        setFormData({
          line1: '',
          line2: '',
          town: '',
          county: '',
          postcode: '',
          property_value: '',
          ownership_type: 'Freehold',
          completion_date: '',
          purchasers: [],
          firstTimeBuyer: false,
          helpToBuyISA: false,
          helpToBuy: false,
          helpToBuyEquityLoan: false,
          rightToBuy: false,
          sharedOwnership: false,
          buyingWithMortgage: false,
          newBuild: false,
          islamicMortgage: false,
          auctionRepossession: false,
          giftedDeposit: '',
          multipleProperties: false,
          buyToLet: false,
          arrangingIndemnityPolicy: false,
          unregisteredProperty: false,
          staircasing: false,
          isNonUKResident: false,
          nonResidentialMixed: false
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load conveyancing data'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleInfoClick = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };
  
  const handleCustomerAdd = (customer) => {
    setFormData(prev => ({
      ...prev,
      purchasers: [...prev.purchasers, customer]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      // Prepare update payload
      const updatePayload = {
        // Property fields
        property_address: {
          address1: formData.line1,
          address2: formData.line2,
          town: formData.town,
          country: formData.county, // Frontend uses 'county' but backend expects 'country'
          postcode: formData.postcode
        },
        property_value: parseFloat(formData.property_value) || 0,
        ownership_type: formData.ownership_type,
        completion_date: formData.completion_date,

        // Partner & Lender
        partner: formData.partner,
        lender_panel: formData.lender_panel,

        // Customer fields
        customer_ids: formData.purchasers.map(p => p._id || p),
        
        // Info fields
        first_time_buyer: formData.firstTimeBuyer,
        help_to_buy_isa: formData.helpToBuyISA,
        help_to_buy: formData.helpToBuy,
        help_to_buy_equity_loan: formData.helpToBuyEquityLoan,
        right_to_buy: formData.rightToBuy,
        shared_ownership: formData.sharedOwnership,
        buying_with_mortgage: formData.buyingWithMortgage,
        new_build: formData.newBuild,
        islamic_mortgage: formData.islamicMortgage,
        auction_repossession: formData.auctionRepossession,
        gifted_deposit: formData.giftedDeposit,
        multiple_properties: formData.multipleProperties,
        buy_to_let: formData.buyToLet,
        arranging_indemnity_policy: formData.arrangingIndemnityPolicy,
        unregistered_property: formData.unregisteredProperty,
        staircasing: formData.staircasing,
        is_non_uk_resident: formData.isNonUKResident,
        non_residential_mixed: formData.nonResidentialMixed
      };
      
      console.log('Updating with payload:', updatePayload);
      console.log('Has existing conveyancing data:', hasConveyancingData);
      
      let response;
      
      // Always use saveConveyancingPurchaseToLead - it handles both create and update
      console.log('Using saveConveyancingPurchaseToLead endpoint for lead_id:', lead_id);
      console.log('Saving with payload:', updatePayload);
      
      try {
        response = await postRequest(
          `private/saveConveyancingPurchaseToLead/${lead_id}`,
          updatePayload,
          authCtx.token
        );
        console.log('Save response:', response);
      } catch (saveError) {
        console.error('Save error details:', saveError);
        console.error('Save error response:', saveError.response);
        throw saveError;
      }
      
      if (response?.status === 'success' || response?.status === 200 || response?.status === 201) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Conveyancing details saved successfully!'
        });
        
        // Navigate back to view
        navigate(`/leads/view/conveyancing/${lead_id}`);
      } else {
        throw new Error(response?.message || (hasConveyancingData ? 'Update failed' : 'Creation failed'));
      }
    } catch (error) {
      console.error('Error updating:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to update conveyancing details'
      });
    } finally {
      setSaving(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <>
        <BreadcrumbsWrapper
          items={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Leads', path: '/leads' },
            { label: 'Edit Conveyancing', path: '' },
          ]}
        />
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading conveyancing data...</Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Leads', path: '/leads' },
          { label: `Conveyancing ${leadData?.lead_id || lead_id}`, path: `/leads/view/conveyancing/${lead_id}` },
          { label: 'Edit', path: '' },
        ]}
      />
      
      <Box sx={{ p: 3, backgroundColor: '#ffffff' }}>
        <Paper sx={{
          p: 4,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          borderRadius: '12px',
          border: '1px solid rgba(0,0,0,0.04)'
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 4,
            pb: 3,
            borderBottom: '2px solid #f0f4f8'
          }}>
            <Box>
              <Typography variant="h5" sx={{
                fontWeight: '700',
                color: '#1a202c',
                mb: 1,
                fontSize: '28px'
              }}>
                Edit Conveyancing Purchase
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: '4px', height: '4px', backgroundColor: '#6f42c1', borderRadius: '50%' }}></Box>
                <Typography sx={{ fontSize: '13px', color: '#718096', fontWeight: '500' }}>
                  Lead ID: <strong style={{ color: '#2d3748' }}>{leadData?.lead_id || lead_id}</strong>
                </Typography>
              </Box>
            </Box>
          </Box>

          <form onSubmit={handleSubmit}>
            {/* Property Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{
                fontSize: '14px',
                fontWeight: '700',
                mb: 2.5,
                color: '#2d3748',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <Box sx={{ width: '4px', height: '24px', backgroundColor: '#6f42c1', borderRadius: '2px' }}></Box>
                Property Details
              </Typography>

              <Box sx={{
                backgroundColor: '#f7fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                p: 3
              }}>
                <Grid container spacing={2}>
                  {/* Address Fields */}
                  <Grid item xs={12}>
                    <Typography sx={{ fontSize: '12px', fontWeight: '600', color: '#6f42c1', textTransform: 'uppercase', mb: 2, letterSpacing: '0.5px' }}>
                      📍 Property Address
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Address Line 1"
                      placeholder="e.g., 123 Main Street"
                      value={formData.line1}
                      onChange={(e) => handleChange('line1', e.target.value)}
                      size="small"
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          transition: 'all 0.2s ease',
                          '&:hover fieldset': {
                            borderColor: '#cbd5e0'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#6f42c1',
                            boxShadow: '0 0 0 3px rgba(111, 66, 193, 0.1)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Address Line 2"
                      placeholder="e.g., Apartment, Suite, etc."
                      value={formData.line2}
                      onChange={(e) => handleChange('line2', e.target.value)}
                      size="small"
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          transition: 'all 0.2s ease',
                          '&:hover fieldset': {
                            borderColor: '#cbd5e0'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#6f42c1',
                            boxShadow: '0 0 0 3px rgba(111, 66, 193, 0.1)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Town/City"
                      placeholder="e.g., London"
                      value={formData.town}
                      onChange={(e) => handleChange('town', e.target.value)}
                      size="small"
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          transition: 'all 0.2s ease',
                          '&:hover fieldset': {
                            borderColor: '#cbd5e0'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#6f42c1',
                            boxShadow: '0 0 0 3px rgba(111, 66, 193, 0.1)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="County"
                      placeholder="e.g., Greater London"
                      value={formData.county}
                      onChange={(e) => handleChange('county', e.target.value)}
                      size="small"
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          transition: 'all 0.2s ease',
                          '&:hover fieldset': {
                            borderColor: '#cbd5e0'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#6f42c1',
                            boxShadow: '0 0 0 3px rgba(111, 66, 193, 0.1)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Postcode"
                      placeholder="e.g., SW1A 1AA"
                      value={formData.postcode}
                      onChange={(e) => handleChange('postcode', e.target.value)}
                      size="small"
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          transition: 'all 0.2s ease',
                          '&:hover fieldset': {
                            borderColor: '#cbd5e0'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#6f42c1',
                            boxShadow: '0 0 0 3px rgba(111, 66, 193, 0.1)'
                          }
                        }
                      }}
                    />
                  </Grid>

                  {/* Divider */}
                  <Grid item xs={12} sx={{ my: 1 }}>
                    <Box sx={{ height: '1px', backgroundColor: '#e2e8f0' }}></Box>
                  </Grid>

                  {/* Property Value and Details */}
                  <Grid item xs={12}>
                    <Typography sx={{ fontSize: '12px', fontWeight: '600', color: '#6f42c1', textTransform: 'uppercase', mb: 2, letterSpacing: '0.5px' }}>
                      💰 Purchase Details
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Purchase Price"
                      type="number"
                      value={formData.property_value}
                      onChange={(e) => handleChange('property_value', e.target.value)}
                      size="small"
                      variant="outlined"
                      slotProps={{
                        input: {
                          startAdornment: <InputAdornment position="start">£</InputAdornment>,
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          transition: 'all 0.2s ease',
                          '&:hover fieldset': {
                            borderColor: '#cbd5e0'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#6f42c1',
                            boxShadow: '0 0 0 3px rgba(111, 66, 193, 0.1)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small" sx={{
                      '& .MuiOutlinedInput-root': {
                        transition: 'all 0.2s ease',
                      }
                    }}>
                      <InputLabel>Ownership Type</InputLabel>
                      <Select
                        value={formData.ownership_type}
                        onChange={(e) => handleChange('ownership_type', e.target.value)}
                        label="Ownership Type"
                      >
                        <MenuItem value="Freehold">Freehold</MenuItem>
                        <MenuItem value="Leasehold">Leasehold</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Est. Completion Date"
                      type="date"
                      value={formData.completion_date}
                      onChange={(e) => handleChange('completion_date', e.target.value)}
                      size="small"
                      variant="outlined"
                      slotProps={{
                        htmlInput: {
                          type: 'date'
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          transition: 'all 0.2s ease',
                          '&:hover fieldset': {
                            borderColor: '#cbd5e0'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#6f42c1',
                            boxShadow: '0 0 0 3px rgba(111, 66, 193, 0.1)'
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>

            {/* Partner & Lender Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{
                fontSize: '14px',
                fontWeight: '700',
                mb: 2.5,
                color: '#2d3748',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <Box sx={{ width: '4px', height: '24px', backgroundColor: '#6f42c1', borderRadius: '2px' }}></Box>
                Partner & Lender Information
              </Typography>

              <Box sx={{
                backgroundColor: '#f7fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                p: 3
              }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small" sx={{
                      '& .MuiOutlinedInput-root': {
                        transition: 'all 0.2s ease',
                        '&:hover fieldset': {
                          borderColor: '#cbd5e0'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#6f42c1',
                          boxShadow: '0 0 0 3px rgba(111, 66, 193, 0.1)'
                        }
                      }
                    }}>
                      <InputLabel>Partner</InputLabel>
                      <Select
                        value={formData.partner}
                        onChange={(e) => handleChange('partner', e.target.value)}
                        label="Partner"
                      >
                        <MenuItem value="">-- Select Partner --</MenuItem>
                        <MenuItem value="ASN Mortgage Solution">ASN Mortgage Solution</MenuItem>
                        <MenuItem value="Partner 1">Partner 1</MenuItem>
                        <MenuItem value="Partner 2">Partner 2</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small" sx={{
                      '& .MuiOutlinedInput-root': {
                        transition: 'all 0.2s ease',
                        '&:hover fieldset': {
                          borderColor: '#cbd5e0'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#6f42c1',
                          boxShadow: '0 0 0 3px rgba(111, 66, 193, 0.1)'
                        }
                      }
                    }}>
                      <InputLabel>Lender Panel</InputLabel>
                      <Select
                        value={formData.lender_panel}
                        onChange={(e) => handleChange('lender_panel', e.target.value)}
                        label="Lender Panel"
                      >
                        <MenuItem value="">-- Select Lender Panel --</MenuItem>
                        <MenuItem value="Accord">Accord</MenuItem>
                        <MenuItem value="Lender 1">Lender 1</MenuItem>
                        <MenuItem value="Lender 2">Lender 2</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            </Box>

            {/* Purchasers Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{
                fontSize: '14px',
                fontWeight: '700',
                mb: 2.5,
                color: '#2d3748',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <Box sx={{ width: '4px', height: '24px', backgroundColor: '#6f42c1', borderRadius: '2px' }}></Box>
                Purchasers ({formData.purchasers.length})
              </Typography>

              <Box sx={{
                backgroundColor: '#f7fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                p: 3
              }}>
                {formData.purchasers.length > 0 ? (
                  <Box sx={{ mb: 2 }}>
                    {formData.purchasers.map((purchaser, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          backgroundColor: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          p: 2,
                          mb: 1.5,
                          '&:hover': {
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                          }
                        }}
                      >
                        <Box>
                          <Typography sx={{ fontWeight: '600', color: '#2d3748', fontSize: '14px' }}>
                            {typeof purchaser === 'object'
                              ? `${purchaser.title || ''} ${purchaser.forename || ''} ${purchaser.surname || ''}`.trim()
                              : `Customer ${index + 1}`}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                            {purchaser.email && (
                              <Typography sx={{ fontSize: '12px', color: '#718096' }}>
                                📧 {purchaser.email}
                              </Typography>
                            )}
                            {purchaser.phone_number && (
                              <Typography sx={{ fontSize: '12px', color: '#718096' }}>
                                📱 {purchaser.phone_number}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              purchasers: prev.purchasers.filter((_, i) => i !== index)
                            }));
                          }}
                          sx={{ textTransform: 'none' }}
                        >
                          Remove
                        </Button>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: '13px', color: '#a0aec0', textAlign: 'center', py: 2 }}>
                    No purchasers added yet
                  </Typography>
                )}

                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setShowCustomerModal(true)}
                  sx={{
                    width: '100%',
                    backgroundColor: '#6f42c1',
                    '&:hover': {
                      backgroundColor: '#5a35a3'
                    },
                    textTransform: 'none',
                    fontWeight: '500'
                  }}
                >
                  Add Purchaser
                </Button>
              </Box>
            </Box>
            
            {/* Info Section - Organized into Categories */}
            <Typography variant="h6" sx={{
              fontSize: '14px',
              fontWeight: '700',
              mb: 2.5,
              color: '#2d3748',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <Box sx={{ width: '4px', height: '24px', backgroundColor: '#6f42c1', borderRadius: '2px' }}></Box>
              Buyer & Property Details
            </Typography>

            <Box sx={{
              backgroundColor: '#f7fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              p: 3,
              mb: 4
            }}>
              {/* Buyer Assistance Schemes */}
              <Box sx={{ mb: 3, pb: 3, borderBottom: '1px solid #e2e8f0' }}>
                <Typography sx={{ fontSize: '12px', fontWeight: '600', color: '#6f42c1', textTransform: 'uppercase', mb: 2, letterSpacing: '0.5px' }}>
                  💷 Buyer Assistance & Schemes
                </Typography>
                <Grid container spacing={1}>
                  {[
                    { label: 'First Time Buyer', field: 'firstTimeBuyer' },
                    { label: 'Help to Buy ISA', field: 'helpToBuyISA' },
                    { label: 'Help to Buy', field: 'helpToBuy' },
                    { label: 'Help to Buy Equity Loan', field: 'helpToBuyEquityLoan' },
                    { label: 'Right to Buy', field: 'rightToBuy' },
                    { label: 'Shared Ownership', field: 'sharedOwnership' }
                  ].map((item) => (
                    <Grid item xs={12} sm={6} md={4} key={item.field}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ToggleButton
                          value={item.field}
                          selected={formData[item.field]}
                          onChange={() => handleInfoClick(item.field)}
                          sx={{
                            width: '100%',
                            padding: '10px 12px',
                            textTransform: 'none',
                            fontSize: '13px',
                            border: '1px solid #ced4da',
                            justifyContent: 'flex-start',
                            color: formData[item.field] ? 'white' : '#495057',
                            backgroundColor: formData[item.field] ? '#6f42c1' : '#f8f9fa',
                            '&.Mui-selected': {
                              backgroundColor: '#6f42c1',
                              color: 'white',
                              '&:hover': {
                                backgroundColor: '#5a35a3',
                              },
                            },
                            '&:hover': {
                              backgroundColor: formData[item.field] ? '#5a35a3' : '#e9ecef',
                            },
                          }}
                        >
                          <Box sx={{ mr: 1 }}>
                            {formData[item.field] ? '✓' : '◯'}
                          </Box>
                          {item.label}
                        </ToggleButton>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* Mortgage & Finance Details */}
              <Box sx={{ mb: 3, pb: 3, borderBottom: '1px solid #e0e0e0' }}>
                <Typography sx={{ fontSize: '12px', fontWeight: '600', color: '#6f42c1', textTransform: 'uppercase', mb: 2, letterSpacing: '0.5px' }}>
                  🏦 Mortgage & Finance
                </Typography>
                <Grid container spacing={1}>
                  {[
                    { label: 'Buying with Mortgage', field: 'buyingWithMortgage' },
                    { label: 'Islamic Mortgage', field: 'islamicMortgage' },
                    { label: 'Auction / Repossession', field: 'auctionRepossession' }
                  ].map((item) => (
                    <Grid item xs={12} sm={6} md={4} key={item.field}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ToggleButton
                          value={item.field}
                          selected={formData[item.field]}
                          onChange={() => handleInfoClick(item.field)}
                          sx={{
                            width: '100%',
                            padding: '10px 12px',
                            textTransform: 'none',
                            fontSize: '13px',
                            border: '1px solid #ced4da',
                            justifyContent: 'flex-start',
                            color: formData[item.field] ? 'white' : '#495057',
                            backgroundColor: formData[item.field] ? '#6f42c1' : '#f8f9fa',
                            '&.Mui-selected': {
                              backgroundColor: '#6f42c1',
                              color: 'white',
                              '&:hover': {
                                backgroundColor: '#5a35a3',
                              },
                            },
                            '&:hover': {
                              backgroundColor: formData[item.field] ? '#5a35a3' : '#e9ecef',
                            },
                          }}
                        >
                          <Box sx={{ mr: 1 }}>
                            {formData[item.field] ? '✓' : '◯'}
                          </Box>
                          {item.label}
                        </ToggleButton>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* Property Type & Structure */}
              <Box sx={{ mb: 3, pb: 3, borderBottom: '1px solid #e0e0e0' }}>
                <Typography sx={{ fontSize: '12px', fontWeight: '600', color: '#6f42c1', textTransform: 'uppercase', mb: 2, letterSpacing: '0.5px' }}>
                  🏠 Property Type & Structure
                </Typography>
                <Grid container spacing={1}>
                  {[
                    { label: 'New Build', field: 'newBuild' },
                    { label: 'Buy to Let', field: 'buyToLet' },
                    { label: 'Multiple Properties', field: 'multipleProperties' },
                    { label: 'Unregistered Property', field: 'unregisteredProperty' },
                    { label: 'Non-Residential / Mixed Use', field: 'nonResidentialMixed' }
                  ].map((item) => (
                    <Grid item xs={12} sm={6} md={4} key={item.field}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ToggleButton
                          value={item.field}
                          selected={formData[item.field]}
                          onChange={() => handleInfoClick(item.field)}
                          sx={{
                            width: '100%',
                            padding: '10px 12px',
                            textTransform: 'none',
                            fontSize: '13px',
                            border: '1px solid #ced4da',
                            justifyContent: 'flex-start',
                            color: formData[item.field] ? 'white' : '#495057',
                            backgroundColor: formData[item.field] ? '#6f42c1' : '#f8f9fa',
                            '&.Mui-selected': {
                              backgroundColor: '#6f42c1',
                              color: 'white',
                              '&:hover': {
                                backgroundColor: '#5a35a3',
                              },
                            },
                            '&:hover': {
                              backgroundColor: formData[item.field] ? '#5a35a3' : '#e9ecef',
                            },
                          }}
                        >
                          <Box sx={{ mr: 1 }}>
                            {formData[item.field] ? '✓' : '◯'}
                          </Box>
                          {item.label}
                        </ToggleButton>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* Additional Considerations */}
              <Box>
                <Typography sx={{ fontSize: '12px', fontWeight: '600', color: '#6f42c1', textTransform: 'uppercase', mb: 2, letterSpacing: '0.5px' }}>
                  ⚙️ Additional Considerations
                </Typography>
                <Grid container spacing={1}>
                  {[
                    { label: 'Gifted Deposit - Num Giftors', field: 'giftedDeposit', hasInput: true },
                    { label: 'Arranging Indemnity Policy', field: 'arrangingIndemnityPolicy' },
                    { label: 'Staircasing', field: 'staircasing' },
                    { label: 'Is Non-UK Resident', field: 'isNonUKResident' }
                  ].map((item) => (
                    <Grid item xs={12} sm={6} md={4} key={item.field}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ToggleButton
                          value={item.field}
                          selected={formData[item.field]}
                          onChange={() => handleInfoClick(item.field)}
                          sx={{
                            flex: 1,
                            padding: '10px 12px',
                            textTransform: 'none',
                            fontSize: '13px',
                            border: '1px solid #ced4da',
                            justifyContent: 'flex-start',
                            color: formData[item.field] ? 'white' : '#495057',
                            backgroundColor: formData[item.field] ? '#6f42c1' : '#f8f9fa',
                            '&.Mui-selected': {
                              backgroundColor: '#6f42c1',
                              color: 'white',
                              '&:hover': {
                                backgroundColor: '#5a35a3',
                              },
                            },
                            '&:hover': {
                              backgroundColor: formData[item.field] ? '#5a35a3' : '#e9ecef',
                            },
                          }}
                        >
                          <Box sx={{ mr: 1 }}>
                            {formData[item.field] ? '✓' : '◯'}
                          </Box>
                          {item.label}
                        </ToggleButton>
                        {item.hasInput && formData[item.field] && (
                          <TextField
                            type="number"
                            placeholder="Num"
                            value={formData.giftedDeposit}
                            onChange={(e) => handleChange('giftedDeposit', e.target.value)}
                            size="small"
                            sx={{ width: '80px' }}
                            slotProps={{
                              htmlInput: { min: '0' }
                            }}
                          />
                        )}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box sx={{
              display: 'flex',
              gap: 2,
              pt: 4,
              mt: 4,
              borderTop: '2px solid #f0f4f8'
            }}>
              <Button
                variant="outlined"
                onClick={() => navigate(`/leads/view/conveyancing/${lead_id}`)}
                sx={{
                  borderColor: '#cbd5e0',
                  color: '#4a5568',
                  fontWeight: '600',
                  textTransform: 'none',
                  padding: '10px 24px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#a0aec0',
                    backgroundColor: '#f7fafc',
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={saving}
                sx={{
                  backgroundColor: '#6f42c1',
                  fontWeight: '600',
                  textTransform: 'none',
                  padding: '10px 32px',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(111, 66, 193, 0.3)',
                  '&:hover': {
                    backgroundColor: '#5a35a3',
                    boxShadow: '0 6px 16px rgba(111, 66, 193, 0.4)',
                  },
                  '&:disabled': {
                    backgroundColor: '#cbd5e0',
                    boxShadow: 'none'
                  }
                }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>
      
      {/* Customer Search Modal */}
      <CustomerSearchModal
        show={showCustomerModal}
        onHide={() => setShowCustomerModal(false)}
        authToken={authCtx.token}
        onCustomerAdd={handleCustomerAdd}
        selectedCustomers={formData.purchasers}
      />
    </>
  );
}